import {
    IBookingService,
    IShowBookingDetailsParams,
    IRetrieveBookingParams,
    ISearchBookingByEmailParams,
    ISearchBookingByLastNameParams} from "./booking.service.interface";
import {makeObservable, observable, reaction, runInAction} from "mobx";
import {IServiceFactory} from "../service-factory.interface";
import {AvailabilityModel} from "../flight-search/models/availability.model";
import {ServiceBase} from "../service-base";
import {IBookingStrategy} from "./booking-strategies/booking-strategy.interface";
import {NoBookingStrategy} from "./booking-strategies/no-strategy/no-booking.strategy";
import {IDotRezBookingSession} from "../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";

import {IDotRezBookingData} from "../dot-rez-api/data-contracts/booking/booking-state/booking-state.data-contracts";
import {IRoundTripLowFareReader} from "../low-fare/low-fare-readers/low-fare-reader.interface";
import {
    IFlightSearchControllerViewModel
} from "../flight-search/flight-search-controller/flight-search-controller-view-model.interface";
import {BookingModel} from "./models/booking.model";
import {BookingStrategyFactory} from "./booking-strategies/booking-strategy.factory";
import {IWizard} from "../../models/wizard/wizard.interface";
import {
    IRouteActivationOptions,
    IRoutingGuard,
    RoutingGuardContext,
    RoutingGuardResult
} from "../navigation/navigator.service.interface";
import {ManageMyBookingStrategy} from "./booking-strategies/manage-my-booking/manage-my-booking.strategy";
import {DotRezDomainCodes} from "../dot-rez-api/data-contracts/enums/domain-codes.enum";
import {ClientBroadcastChannel} from "../../utils/client-broadcast-channel";
import {BookingServiceBroadcastMessages} from "./booking-service-broadcast-messages";
import {BroadcastChannelsEnum} from "../../utils/broadcast-channels.enum";
import {UserSessionStorageKeys} from "../storage/user-session-storage/user-session-storage-keys";
import {BookingStatusDeeplinkActionEnum} from "../deep-links/booking-status-deeplink-query-params.enum";

interface IRetrieveBookingStrategyOptions {
    retrieveBookingState: (bookingSession: IDotRezBookingSession) => Promise<IDotRezBookingData>;
    createBookingStrategy: (bookingSession: IDotRezBookingSession, bookingData: IDotRezBookingData) => Promise<IBookingStrategy>;
    useTransientBookingSession?: boolean;
}

export class BookingService extends ServiceBase implements IBookingService, IRoutingGuard {
    constructor(services: IServiceFactory) {
        super(services);

        this.services.navigator.registerRoutingGuard(this);

        this._broadcastChannel = new ClientBroadcastChannel<BookingServiceBroadcastMessages>(BroadcastChannelsEnum.BookingService, this.services)
        this._broadcastChannel.subscribe(this._broadcastChannelMessageHandler)
        this._strategyFactory = new BookingStrategyFactory(services);

        this._currentStrategy = new NoBookingStrategy(services);
        makeObservable(this, {
            _currentStrategy: observable.ref,
            _isBookingRestoreInProgress: observable.ref
        });

        reaction(() => this.services.navigator.currentRoute,
            async () => {
                if(!this.services.navigator.isOnBookingRoute) {
                    if(this.isThereABookingInProgress) {
                        await this._setCurrentStrategy(new NoBookingStrategy(this.services));
                    }

                }
            });

        this._tryRestore()
            .catch(err => {
                this.services.logger.debug('Failed to restore booking strategy', err)
            })
            .finally(() => {
                runInAction(() => {
                    this._isBookingRestoreInProgress = false;
                });
            });
    }

    async canNavigate(context: RoutingGuardContext): Promise<RoutingGuardResult> {
        if(this.services.navigator.isBookingRoute(context.targetLocation.pathname) && !this.isThereABookingInProgress) {
            return RoutingGuardResult.Block;
        }

        return RoutingGuardResult.Allow;


    }

    private readonly _strategyFactory: BookingStrategyFactory;
    private readonly _broadcastChannel: ClientBroadcastChannel<BookingServiceBroadcastMessages>;

    private _broadcastChannelMessageHandler = async (message: BookingServiceBroadcastMessages) => {
        switch(message.type) {
            case "bookingLoaded":
                if(this.isThereABookingInProgress ) {
                    if(message.payload.dotRezToken === this.current.token
                        || (message.payload.recordLocator && (message.payload.recordLocator === this.current.recordLocator))) {
                        await this.services.navigator.goHome({
                            skipRoutingGuards: true
                        });
                    }
                }

                break;
        }
    }

    private async _tryRestore(): Promise<void> {
        if(this.services.navigator.routes.booking.hasActiveChildRoute) {
            await this._tryRestoreStrategy(() => this._strategyFactory.tryRestoreBookingStrategy());
        } else if(this.services.navigator.routes.manageMyBooking.hasActiveChildRoute) {
            if(!await this._tryRestoreManageMyBookingByDeepLink()) {
                await this._tryRestoreStrategy(async () => {
                    return await this._strategyFactory.tryRestoreManageMyBookingStrategy();
                });
            }
        } else if(this.services.navigator.routes.checkIn.hasActiveChildRoute) {
            await this._tryRestoreStrategy(() => this._strategyFactory.tryRestoreCheckInStrategy());
        } else if(this.services.navigator.routes.flightItinerary.isActive) {
            await this._tryRestoreStrategy(() => this._strategyFactory.tryRestoreViewBookingDetailsStrategy());
        }
    }

    private async _tryRestoreManageMyBookingByDeepLink(): Promise<boolean> {
        const token = this.services.userSessionStorage.getItem(UserSessionStorageKeys.ActiveBookingToken);
        const deepLinkParams = ManageMyBookingStrategy.getCurrentDeepLinkParams(this.services);

        if(!token && deepLinkParams?.recordLocator && deepLinkParams?.email) {
            try {
                await this.startManageMyBookingByEmail({
                    recordLocator: deepLinkParams.recordLocator,
                    emailAddress: deepLinkParams.email
                }, null);
                await this.current.paymentHandler.loadPaymentMethods({
                    showLoadingIndicator: true
                });
                return true;
            } catch (err) {
                this.services.logger.error(`Failed to restore manage my booking by deeplink! email = ${deepLinkParams.email} | pnr = ${deepLinkParams.recordLocator}`, err);
            }
        }

        return false;
    }

    private async _tryRestoreStrategy(restoreAction: () => Promise<IBookingStrategy | null>): Promise<void> {

        await this.services.loadingIndicator.execute({
            action: async () => {
                const strategy = await restoreAction();
                if(strategy !== null) {
                    await this._setCurrentStrategy(strategy)
                } else {
                    await this.services.navigator.goHome({
                        skipRoutingGuards: true
                    });
                }
            },
            displayExceptionHandler: () => {
                //no need to display anything to the user
                //in case the restore booking state fails when the user refresh the page we don't want to show him anything
            }
        });

    }

    get isThereABookingInProgress(): boolean {
        return Boolean(this._currentStrategy)
            && !(this._currentStrategy instanceof NoBookingStrategy);
    }

    _isBookingRestoreInProgress: boolean = true;
    get isBookingRestoreInProgress(): boolean {
        if(this._isBookingRestoreInProgress) {
            return true;
        }

        if(this.isThereABookingInProgress) {
            return this.current.isBookingDiscardChangesInProgress;
        }

        return false;
    }

    get currentPageCanBeRendered(): boolean {
        return !this.isBookingRestoreInProgress && this.isThereABookingInProgress;
    }



    get steps(): IWizard {
        return this._currentStrategy.steps;
    }

    get searchController(): IFlightSearchControllerViewModel {
        return this._currentStrategy.flightSearchController;
    }

    _currentStrategy: IBookingStrategy;
    get current(): BookingModel {
        return this._currentStrategy.booking;
    }

    get activeCurrency(): string {
        return this._currentStrategy.activeCurrency;
    }

    get availability(): AvailabilityModel {
        return this._currentStrategy.availability;
    }

    private async _setCurrentStrategy(newStrategy: IBookingStrategy): Promise<void> {
        try {
            await this._currentStrategy.dispose();
        } catch (err) {
            this.services.logger.warning('Failed to reset the booking', err);
        }

        runInAction(() => {
            this._currentStrategy = newStrategy;
        });

        if(this.isThereABookingInProgress) {
            this._broadcastChannel.sendMessage({
                type: "bookingLoaded",
                payload: {
                    dotRezToken: this.current.token,
                    recordLocator: this.current.recordLocator
                }
            })
        }
    }

    async startNewBooking(searchController: IFlightSearchControllerViewModel, activateOptions?: IRouteActivationOptions): Promise<void> {



        await this.services.loadingIndicator.execute({
            showPromotionalMessage: true,
            action: async () => {

                const token = (await searchController.getFlightSearchSession()).token;
                const bookingSession = this.services.dotRezApi.createBookingSession(token);

                this.services.userSessionStorage.removeItem(UserSessionStorageKeys.ActiveBookingSearchControllerStorage);
                searchController = searchController.clone({
                    storageKey: UserSessionStorageKeys.ActiveBookingSearchControllerStorage,
                    bookingSession: bookingSession
                });

                if(!searchController.returnDate) {
                    searchController.isOneWayDepartureTrip = true;
                }

                await searchController.applySearch();
                const strategy = await this._strategyFactory.startNewBookingStrategy(bookingSession, searchController);
                await this._setCurrentStrategy(strategy);
            }
        });


        this.steps.start(activateOptions);
    }

    private async _startCheckIn(retrieveBookingState: (bookingSession: IDotRezBookingSession) => Promise<IDotRezBookingData>, routeActivationOptions?: IRouteActivationOptions): Promise<void> {

        await this.services.loadingIndicator.execute({
            action: async () => {
                const strategy = await this._retrieveBookingStrategy({
                    retrieveBookingState: retrieveBookingState,
                    createBookingStrategy: (bookingSession, bookingData) => this._strategyFactory.createCheckInStrategy(bookingSession, bookingData)
                });

                if(strategy) {
                    await this._setCurrentStrategy(strategy);
                    this.steps.start(routeActivationOptions);
                }
            }
        });
    }

    private async _retrieveBookingStrategy(options: IRetrieveBookingStrategyOptions): Promise<IBookingStrategy | null> {

        let bookingSession:IDotRezBookingSession;
        if(options.useTransientBookingSession) {
            bookingSession = await this.services.user.createTransientBookingSession();
        } else {
            bookingSession = await this.services.user.createBookingSession({
                forDog: false
            });
        }
        let bookingData: IDotRezBookingData;
        try {
            bookingData = await options.retrieveBookingState(bookingSession);
        } catch (err) {
            bookingSession.dispose();
            this.services.alert.showError(this.services.language.translate(`We couldn't find any booking for the specified criteria. Please try again`));
            return null;
        }

        try {
            return await options.createBookingStrategy(bookingSession, bookingData);
        } catch (err) {
            bookingSession.dispose();
            throw err;
        }

    }


    async startCheckInByLastName(searchParams: ISearchBookingByLastNameParams): Promise<void> {
        await this._startCheckIn(session => session.bringBookingInStateByLastName(searchParams), searchParams.routeActivationOptions);
    }

    async startCheckInByEmail(searchParams: ISearchBookingByEmailParams): Promise<void> {
        await this._startCheckIn(session => session.bringBookingInStateByEmail(searchParams), searchParams.routeActivationOptions);
    }

    async startCheckInWithFallback(searchParams: IRetrieveBookingParams): Promise<void> {
        await this._startCheckIn(session => this._retrieveBookingWithFallBack(searchParams, session), searchParams.routeActivationOptions);
    }

    private async _startManageMyBooking(retrieveBookingState: (bookingSession: IDotRezBookingSession) => Promise<IDotRezBookingData>,
                                        deepLinkAction: BookingStatusDeeplinkActionEnum | null,
                                        activateOptions?: IRouteActivationOptions): Promise<void> {
        await this.services.loadingIndicator.execute({
            action: async () => {
                const strategy = await this._retrieveBookingStrategy({
                    retrieveBookingState: retrieveBookingState,
                    createBookingStrategy: (bookingSession, bookingData) => this._strategyFactory.createManageMyBookingStrategy(bookingSession, bookingData)
                });

                if(strategy) {
                    await this._setCurrentStrategy(strategy);
                    this.steps.start(activateOptions);
                    this._executeManageMyBookingDeepLinkAction(strategy.booking, deepLinkAction);

                }
            }
        });
    }

    private _executeManageMyBookingDeepLinkAction(booking: BookingModel, deepLinkAction: BookingStatusDeeplinkActionEnum | null) {
        if(!deepLinkAction) {
            return;
        }

        setTimeout(async () => {
            switch(deepLinkAction) {
                case BookingStatusDeeplinkActionEnum.Seats:
                    await booking.manageMyBooking.seats.edit();
                    break;
            }
        }, 500);
    }

    async startManageMyBookingByLastName(searchParams: ISearchBookingByLastNameParams, deepLinkAction: BookingStatusDeeplinkActionEnum | null): Promise<void> {
        await this._startManageMyBooking(session => session.bringBookingInStateByLastName(searchParams), deepLinkAction);
    }

    async startManageMyBookingByEmail(searchParams: ISearchBookingByEmailParams, deepLinkAction: BookingStatusDeeplinkActionEnum | null): Promise<void> {
        await this._startManageMyBooking(session => session.bringBookingInStateByEmail(searchParams), deepLinkAction);
    }

    async startManageMyBookingWithFallback(searchParams: IRetrieveBookingParams, deepLinkAction: BookingStatusDeeplinkActionEnum | null): Promise<void> {
        await this._startManageMyBooking(session => this._retrieveBookingWithFallBack(searchParams, session), deepLinkAction);
    }

    private async _retrieveBookingWithFallBack(searchParams: IRetrieveBookingParams, session: IDotRezBookingSession): Promise<IDotRezBookingData> {
        try {
            return await session.bringBookingInStateByEmail(searchParams);
        } catch (err) {
            this.services.logger.error(`Failed to retrieve booking by email = ${searchParams.emailAddress} | recordLocator = ${searchParams.recordLocator}`)
            return await session.bringBookingInStateByLastName(searchParams);
        }
    }

    async showFlightItinerary(searchParams: IShowBookingDetailsParams): Promise<void> {
        const token = await this.services.loadingIndicator.execute({
            action: async () => {
                let retrieveBookingState: (bookingSession: IDotRezBookingSession) => Promise<IDotRezBookingData>;
                if(searchParams.emailAddress) {
                    const emailAddress = searchParams.emailAddress;
                    retrieveBookingState = session => this._retrieveBookingWithFallBack({
                        ...searchParams,
                        emailAddress: emailAddress
                    }, session)
                } else {
                    retrieveBookingState = session => session.bringBookingInStateByLastName(searchParams)
                }
                const strategy = await this._retrieveBookingStrategy({
                        useTransientBookingSession: true,
                        retrieveBookingState: retrieveBookingState,
                        createBookingStrategy: (bookingSession, bookingData) => this._strategyFactory.createViewBookingDetailsStrategy(bookingSession, bookingData)
                    }
                );

                if(strategy) {
                    await this._setCurrentStrategy(strategy);
                }

                return strategy?.booking.token || null;
            }
        });

        if(token) {
            this.services.navigator.routes.flightItinerary.activate();
        }
    }

    async upgradeCurrentBookingSessionToAuthorizedUser(userName: string, password: string, domain: DotRezDomainCodes, previousUserNameHash: string): Promise<void> {
        await this._currentStrategy.upgradeCurrentBookingSessionToAuthorizedUser(userName, password, domain, previousUserNameHash);
    }

    getLowFareReader(): IRoundTripLowFareReader {
        return this._currentStrategy.getLowFaresReader();
    }

    async createTransientBookingStrategy(bookingSession: IDotRezBookingSession, bookingData: IDotRezBookingData): Promise<IBookingStrategy> {
        return this._strategyFactory.createTransientBookingStrategy(bookingSession, bookingData);
    }

    async switchToManageMyBooking(): Promise<void> {
        if(!this.isThereABookingInProgress) {
            return;
        }

        const searchParams: ISearchBookingByLastNameParams = {
            lastName: this.current.passengers[0].name.last!,
            recordLocator: this.current.recordLocator!
        }

        if(!this.services.layout.shouldUseWebappLayout) {
            await this.services.navigator.goHome({
                skipRoutingGuards: true
            });
        }

        await this._startManageMyBooking(session => session.bringBookingInStateByLastName(searchParams), null, {
            allowBack: !this.services.layout.shouldUseWebappLayout
        });
    }

    async switchToFlightItinerary(): Promise<void> {
        if(!this.isThereABookingInProgress) {
            return;
        }

        const searchParams: IShowBookingDetailsParams = {
            emailAddress: this.current.contact.fields.emailAddress.value!,
            lastName: this.current.passengers[0].name.last!,
            recordLocator: this.current.recordLocator!
        }

        await this.showFlightItinerary(searchParams)
    }

    async getCommitBookingChannel(): Promise<string> {
        const osName = await this.services.device.getOperatingSystemName();
        return `${this.getPaymentChannel()}_${osName}`;
    }

    getPaymentChannel(): string {
        if(this.services.device.isHybrid) {
            return "MobileApp";
        } else {
            return "Website";
        }
    }
}
